<template lang='pug'>
Drawer.order-instructions-drawer(:title="__('Order Instructions')" @hide="$emit('hide')")
  drawer-contents
    template(v-if="loading")
      Spinner.spin(size="m")
    template(v-else)
      .sub-title
        | {{ __('Choose the item you need instructions for:') }}
      .instructions-wrapper
        .instructions-item(v-for="product in instructionsData" :key="product.parentSku")
          .item-details
            StaticImage.thumb(:src="product.thumbnail" alt="" eager)
            .product-details
              .name {{ product.name }}
              span.sku {{ product.parentSku }}
          .product-instructions
            .instruction(v-for="instruction in product.instructions" :key="instruction.Name")
              RogueButton.link(color="gray" width="full" :link="instruction.Url" :newTab="true")
                | {{ instruction.Description.replace('Customer', '').replace('Instructions', '').trim() || instruction.Sku }}
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { IOrder, IOrderItem } from '@rogue/universal'
import { useRogue } from '@/js/services/Rogue'
import DrawerContents from '@/js/components/drawer-contents.vue'

const rogue = useRogue()

const props = defineProps<{
  order: IOrder
}>()

const instructionsData = ref<Record<string, { sku: string, childSku: string, thumbnail?: string, name?: string, instructions?: string[] | undefined }>>({})
const loading = ref<boolean>(false)

interface InstructionDocument {
  Sku: string;
  Name: string;
  Description: string;
  Url: string;
  IsActive: boolean;
}

interface ProcessedItem {
  childSku: string;
  thumbnail?: string;
  name?: string;
  instructions?: InstructionDocument[];
}

function processOrderItems(items: IOrderItem[]): Record<string, ProcessedItem> {
  const result: Record<string, ProcessedItem> = {}

  function processItem(item: IOrderItem): void {
    const { sku, thumbnail, name, children } = item

    // If no children, this is a leaf item
    if (!children?.length) {
      result[sku] = { childSku: sku, thumbnail, name }

      return
    }

    // Process children
    children.forEach(child => {
      if (child.children?.length) {
        // Child has its own children, recurse
        processItem(child)
      } else {
        // Child is a leaf, associate it with the parent
        result[sku] = {
          childSku: child.sku,
          thumbnail,
          name,
        }
      }
    })
  }

  items.forEach(processItem)

  return result
}

async function fetchInstructions(skus: string[]): Promise<Record<string, InstructionDocument[]> | null> {
  if (skus.length === 0) return null

  try {
    const response = await rogue.fetch(
      `/svc_content/catalog/product/instructions?sku=${skus.join(',')}`
    )

    return response as Record<string, InstructionDocument[]>
  } catch (error) {
    console.error('Error fetching instructions:', error)

    return null
  }
}

function deduplicateInstructions(instructions: InstructionDocument[]): InstructionDocument[] {
  const seen = new Set<string>()

  return instructions.filter(instruction => {
    // Use Name as the unique identifier since it appears to be the primary key
    if (seen.has(instruction.Name)) {
      return false
    }
    seen.add(instruction.Name)

    return true
  })
}

function attachInstructions(
  processedItems: Record<string, ProcessedItem>,
  instructionsResponse: Record<string, InstructionDocument[]>
): void {
  const customerInstructionRegex = /.+CUST_INSTR.+\.pdf/

  Object.entries(processedItems).forEach(([parentSku, item]) => {
    const instructions = instructionsResponse[item.childSku]

    if (!instructions?.length) {
      return
    }

    // Filter for customer instruction documents
    const customerInstructions = instructions.filter(instruction => customerInstructionRegex.test(instruction.Name))

    if (customerInstructions.length > 0) {
      // Deduplicate instructions before assigning
      item.instructions = deduplicateInstructions(customerInstructions)
    }
  })
}

async function populateInstructionsDrawer() {
  instructionsData.value = {}

  if (!props.order?.items?.length) {
    return
  }

  loading.value = true

  try {
    // Process order items to find leaf SKUs that will have instructions
    const processedItems = processOrderItems(props.order.items)

    if (Object.keys(processedItems).length === 0) {
      return
    }

    // Fetch instructions for all child SKUs
    const childSkus = Object.values(processedItems).map(item => item.childSku)
    const instructionsResponse = await fetchInstructions(childSkus)

    if (!instructionsResponse) {
      return
    }

    // Attach instructions to processed items
    attachInstructions(processedItems, instructionsResponse)

    // Filter items that have instructions and format for UI
    const itemsWithInstructions = Object.entries(processedItems)
      .filter(([, item]) => item.instructions?.length)
      .map(([parentSku, item]) => ({
        parentSku,
        ...item,
      }))

    if (itemsWithInstructions.length > 0) {
      instructionsData.value = itemsWithInstructions
    }
  } catch (error) {
    console.error('Error populating instructions drawer:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  populateInstructionsDrawer()
})
</script>

<style lang="scss" scoped>
.sub-title {
  font-size: $size-xs;
  line-height: $size-m;
  margin-bottom: $space-s;
}
.instructions-item {
  margin: $space-l 0;

  .item-details {
    display: flex;
    margin: $space-m 0;

    .product-details {
      margin-left: $space-s;

      .name {
        font-size: $size-s;
        font-weight: bold;
        text-transform: uppercase;
      }

      .sku {
        font-size: $size-xxs;
        color: $color-gray;
      }
    }
  }

  .product-title {
    font-weight: bold;
    line-height: $size-l;
  }

  .thumb {
    width: 83px;
    height: 83px;
  }

  .instruction {
    .link {
      margin: $space-xs 0;
      text-decoration: none;
    }
  }
}
</style>
